// Pure view layer for the Passing Profile module — ported from js/renderers-passingprofile.js
// (the wheel geometry, the frame, the compass/tick labels, the wedge buckets + stats, the
// line data + tooltip, the magnetic radii). DOM-free and unit-tested; PassingProfileModule
// paints what these return as JSX.
//
// The wheel: every filtered pass re-centred on one shared origin, by its real StatsBomb
// angle (radians, 0 = attacking-forward) and length (yards) — the same two fields Pass Map's
// Length filter and Direction row trust, so a 45yd pass draws at the radius "45yds" means
// on that filter's slider.

import type { ProtoEvent } from "@catbento/shared";
import {
  nextMagneticSegment,
  oppositionLabel,
  passBodyPart,
  passCompleted,
  passOf,
  type EventColorMode,
  type EventContext,
  type MagneticRadii,
} from "../engines/event-map-kit.js";
import { clockMinute } from "../data/match-minute.js";

export const CHART_SIZE = 220;
export const CENTER = CHART_SIZE / 2;
export const MAX_RADIUS = 92;
/** = PASS_FILTERS' passLength domain max. */
export const LENGTH_DOMAIN_MAX = 100;
export const RING_FRACS: readonly number[] = [0.25, 0.5, 0.75, 1];

/** Real angle → wheel point, rotated so forward points UP (a pure rotation, not a
 *  reflection — which physical side is which is unchanged). */
export function wheelPoint(angleRad: number, radiusFrac: number): [number, number] {
  const screenAngle = angleRad - Math.PI / 2;
  const r = radiusFrac * MAX_RADIUS;
  return [CENTER + r * Math.cos(screenAngle), CENTER + r * Math.sin(screenAngle)];
}

/** The 8 reference spokes (exact directions; the cardinal four get an arrow). */
export const WEDGE_DEGREES: readonly number[] = [0, 45, 90, 135, 180, 225, 270, 315];
/** The 8 hoverable wedges span spoke-to-spoke, so their centres sit half a wedge off the
 *  spokes (a spoke is a boundary, not a centre — the legacy's pixel-measured fix). */
export const WEDGE_HIT_DEGREES: readonly number[] = WEDGE_DEGREES.map((deg) => deg + 22.5);

export function wedgeUnitVector(deg: number): [number, number] {
  const screenAngle = (deg * Math.PI) / 180 - Math.PI / 2;
  return [Math.cos(screenAngle), Math.sin(screenAngle)];
}

/** A pie slice ±22.5° either side of `deg` out to the ring — a real hit target across the
 *  whole gap, not a thin spoke. Coordinates to 2dp, verbatim. */
export function hoverWedgePath(deg: number): string {
  const half = 22.5;
  const [x1, y1] = wheelPoint(((deg - half) * Math.PI) / 180, 1);
  const [x2, y2] = wheelPoint(((deg + half) * Math.PI) / 180, 1);
  return `M ${CENTER},${CENTER} L ${x1.toFixed(2)},${y1.toFixed(2)} A ${MAX_RADIUS},${MAX_RADIUS} 0 0 1 ${x2.toFixed(2)},${y2.toFixed(2)} Z`;
}

/** The static frame: rings, 45°-spaced reference spokes (from CENTER), and the wedges. */
export function wheelFrame(): {
  rings: number[];
  refSpokes: { x2: number; y2: number }[];
  wedges: { deg: number; d: string }[];
} {
  const refSpokes: { x2: number; y2: number }[] = [];
  for (let deg = 0; deg < 360; deg += 45) {
    const [x2, y2] = wheelPoint((deg * Math.PI) / 180, 1);
    refSpokes.push({ x2, y2 });
  }
  return {
    rings: RING_FRACS.map((f) => f * MAX_RADIUS),
    refSpokes,
    wedges: WEDGE_HIT_DEGREES.map((deg) => ({ deg, d: hoverWedgePath(deg) })),
  };
}

/** A cardinal arrow sits at the ring's edge, pushed 12px further out along its direction,
 *  anchored so it always grows into open space (never back over the wheel). */
export function compassLabelStyle(deg: number): { left: string; top: string; transform: string } {
  const [ux, uy] = wedgeUnitVector(deg);
  const leftPct = ((CENTER + ux * MAX_RADIUS) / CHART_SIZE) * 100;
  const topPct = ((CENTER + uy * MAX_RADIUS) / CHART_SIZE) * 100;
  const clearance = 12;
  const xPct = ux > 0.3 ? 0 : ux < -0.3 ? -100 : -50;
  const yPct = uy > 0.3 ? 0 : uy < -0.3 ? -100 : -50;
  return {
    left: `${leftPct}%`,
    top: `${topPct}%`,
    transform: `translate(calc(${xPct}% + ${ux * clearance}px), calc(${yPct}% + ${uy * clearance}px))`,
  };
}

/** The four arrows; the word is a hover tooltip only (an unlabelled arrow is a trend, not
 *  a textual claim about which physical side is "left"). */
export const COMPASS_LABELS: readonly { deg: number; glyph: string; tooltip: string }[] = [
  { deg: 0, glyph: "▲", tooltip: "Forward" },
  { deg: 90, glyph: "▶", tooltip: "Right" },
  { deg: 180, glyph: "▼", tooltip: "Backward" },
  { deg: 270, glyph: "◀", tooltip: "Left" },
];

/** "25yd … 100yd" up the forward spoke. */
export function ringTickLabels(): { yd: number; topPct: number }[] {
  return RING_FRACS.map((f) => ({ yd: Math.round(f * LENGTH_DOMAIN_MAX), topPct: 50 - ((f * MAX_RADIUS) / CHART_SIZE) * 100 }));
}

// --- Wedges ---------------------------------------------------------------------------------

/** The wedge a pass's angle falls in — floor(deg / 45), wrapping (spokes are boundaries, not
 *  centres) — as the wedge's centre, e.g. 157.5. Null with no captured angle. */
export function passWedgeDeg(pass: ProtoEvent): number | null {
  const angle = passOf(pass).angle;
  if (typeof angle !== "number") return null;
  const deg = ((((angle * 180) / Math.PI) % 360) + 360) % 360;
  return WEDGE_HIT_DEGREES[Math.floor(deg / 45) % 8]!;
}

export interface WedgeBucket {
  count: number;
  complete: number;
  lengthSum: number;
  lengthCount: number;
}

/** Every pass bucketed per wedge (WEDGE_HIT_DEGREES order): count, completes, length sum. */
export function computeWedgeStats(passes: ProtoEvent[]): WedgeBucket[] {
  const buckets = WEDGE_HIT_DEGREES.map(() => ({ count: 0, complete: 0, lengthSum: 0, lengthCount: 0 }));
  for (const pass of passes) {
    const deg = passWedgeDeg(pass);
    if (deg === null) continue;
    const b = buckets[WEDGE_HIT_DEGREES.indexOf(deg)]!;
    b.count++;
    if (passCompleted(pass)) b.complete++;
    const length = passOf(pass).length;
    if (typeof length === "number") {
      b.lengthSum += length;
      b.lengthCount++;
    }
  }
  return buckets;
}

export const wedgeBucket = (buckets: WedgeBucket[], deg: number): WedgeBucket | undefined => buckets[WEDGE_HIT_DEGREES.indexOf(deg)];

// --- Stats bar ------------------------------------------------------------------------------

export interface WheelStats {
  shown: string;
  success: string;
  avgLength: string;
}

/** The resting bar: "N of TOTAL" once narrowed, success %, average length. */
export function wheelStats(passes: ProtoEvent[], allPasses: ProtoEvent[]): WheelStats {
  const complete = passes.filter(passCompleted).length;
  const pct = passes.length ? (complete / passes.length) * 100 : 0;
  const avgLength = passes.length ? passes.reduce((sum, p) => sum + (passOf(p).length || 0), 0) / passes.length : 0;
  return {
    shown:
      allPasses.length !== passes.length
        ? `${passes.length.toLocaleString()} of ${allPasses.length.toLocaleString()}`
        : `${passes.length.toLocaleString()}`,
    success: `${pct.toFixed(1)}%`,
    avgLength: `${avgLength.toFixed(1)}yd`,
  };
}

/** The wedge-hover preview: "this many of what's shown go this way". */
export function wedgePreviewStats(bucket: WedgeBucket, totalShown: number): WheelStats {
  const pct = bucket.count ? (bucket.complete / bucket.count) * 100 : 0;
  const avgLength = bucket.lengthCount ? bucket.lengthSum / bucket.lengthCount : 0;
  return {
    shown: `${bucket.count.toLocaleString()} of ${totalShown.toLocaleString()}`,
    success: `${pct.toFixed(1)}%`,
    avgLength: `${avgLength.toFixed(1)}yd`,
  };
}

// --- Lines + hover --------------------------------------------------------------------------

export interface WheelLine {
  pass: ProtoEvent;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  rgb: string;
  tooltip: string;
}

/** A spoke from the centre, or null for a pass missing its angle or length (not drawn). */
export function wheelLine(ctx: EventContext, pass: ProtoEvent, mode: EventColorMode): WheelLine | null {
  const { angle, length } = passOf(pass);
  if (typeof angle !== "number" || typeof length !== "number") return null;
  const [x2, y2] = wheelPoint(angle, Math.min(1, length / LENGTH_DOMAIN_MAX));
  const foot = passBodyPart(pass);
  const tooltip = `${oppositionLabel(ctx, pass)}\n${passCompleted(pass) ? "Complete" : "Incomplete"} · ${length.toFixed(1)}yd${foot ? " · " + foot : ""} · ${clockMinute(pass.minute, pass.period)}`;
  return { pass, x1: CENTER, y1: CENTER, x2, y2, rgb: mode.getColor(pass), tooltip };
}

/** Pass Map's radii scaled to the wheel's own 220-unit space. */
export const WHEEL_RADII: MagneticRadii = { select: 4, sticky: 8, switchMargin: 0.6 };

export function nextHoveredWheelLine<T extends { x1: number; y1: number; x2: number; y2: number }>(
  p: { x: number; y: number },
  lines: T[],
  current: T | null,
): T | null {
  return nextMagneticSegment(p, lines, current, WHEEL_RADII);
}
